<template>
  <div class="ri-page">
    <div class="ri-filters">
      <v-select
        v-model="partnerId"
        :items="partners"
        item-text="name"
        item-value="id"
        label="Partner"
        outlined
        dense
        hide-details
        :disabled="loading"
        class="ri-field"
        @change="onPartnerChange"
      />

      <v-text-field
        v-model="search"
        placeholder="Invoice number, category…"
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        hide-details
        clearable
        class="ri-field ri-field--wide"
      />

      <div class="d-flex justify-end">
        <v-btn
          class="accent white--text"
          :disabled="!partnerId"
          @click="billingDialog = true"
        >
          Billing Profile
        </v-btn>
        <v-btn
          class="ml-2 accent white--text"
          :disabled="!partnerId"
          @click="openGenerate"
        >
          <v-icon small left>mdi-plus</v-icon>
          Generate
        </v-btn>
      </div>

      <v-spacer />

      <div v-if="documents.length" class="ri-summary">
        <span
          ><strong>{{ documents.length }}</strong> invoices</span
        >
        <span
          ><strong>{{ money(totalRaised) }}</strong> raised</span
        >
      </div>
    </div>

    <div v-if="loading" class="ri-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="ri-state__text">Loading invoices…</p>
    </div>

    <v-card v-else-if="!partnerId" outlined class="ri-state ri-state--card">
      <v-icon size="44" color="grey lighten-1"
        >mdi-file-document-multiple-outline</v-icon
      >
      <h3 class="ri-state__title">Pick a partner</h3>
      <p class="ri-state__text">
        Every invoice raised for the partner, with the working behind it.
      </p>
    </v-card>

    <v-card
      v-else-if="!documents.length"
      outlined
      class="ri-state ri-state--card"
    >
      <v-icon size="44" color="grey lighten-1">mdi-file-outline</v-icon>
      <h3 class="ri-state__title">No invoices raised yet</h3>
      <p class="ri-state__text">
        Generate one for a collection window — the working is worked out first
        and shown for approval before anything is numbered.
      </p>
      <v-btn small depressed color="primary" class="mt-4" @click="openGenerate">
        <v-icon small left>mdi-plus</v-icon>
        Generate Invoice
      </v-btn>
    </v-card>

    <template v-else>
      <div v-for="run in groupedRuns" :key="run.run_id" class="ri-run">
        <div class="ri-run__head">
          <div>
            <h3 class="ri-run__title">{{ run.periodLabel }}</h3>
            <p class="ri-run__sub">
              {{ run.batchLabel }} · raised {{ date(run.invoiceDate) }} ·
              {{ run.documents.length }} invoices
            </p>
          </div>
          <div class="ri-run__right">
            <span class="ri-run__total">{{ money(run.total) }}</span>
            <v-btn
              x-small
              text
              color="primary"
              @click="downloadWorking(run.run_id)"
            >
              <v-icon x-small left>mdi-table-arrow-down</v-icon>
              Download
            </v-btn>
          </div>
        </div>

        <div class="ri-docs">
          <div
            v-for="document in run.documents"
            :key="document.id"
            class="ri-doc"
            :class="{ 'ri-doc--cancelled': document.status === 'cancelled' }"
          >
            <div class="ri-doc__top">
              <span class="ri-doc__number"
                >{{ document.invoice_number }} -
                {{ document.category_name }}</span
              >
              <v-chip
                v-if="document.status === 'cancelled'"
                x-small
                label
                color="error"
                text-color="white"
              >
                Cancelled
              </v-chip>
            </div>
            <p class="ri-doc__amounts">
              <span class="ri-doc__total">{{
                money(document.total_amount)
              }}</span>
              <span class="ri-doc__split">
                Net Amount: {{ money(document.net_amount) }} · GST :
                {{ money(document.gst_amount) }}
              </span>
            </p>
            <div class="ri-doc__actions">
              <v-btn x-small text @click="preview(document)">
                <v-icon x-small left>mdi-eye-outline</v-icon>
                View
              </v-btn>
              <v-btn x-small text @click="downloadPdf(document)">
                <v-icon x-small left>mdi-file-pdf-box</v-icon>
                PDF
              </v-btn>
              <v-btn
                v-if="document.status !== 'cancelled'"
                x-small
                text
                color="error"
                @click="confirmCancel(document)"
              >
                Cancel
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </template>

    <v-dialog v-model="generateDialog" max-width="880px" persistent scrollable>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary py-3">
          <span>Generate Service Invoices</span>
          <v-spacer />
          <v-btn
            icon
            x-small
            :disabled="generating"
            @click="generateDialog = false"
          >
            <v-icon x-small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="ri-gen">
          <div class="ri-gen__filters">
            <v-select
              v-model="form.batch"
              :items="batchItems"
              item-text="label"
              item-value="value"
              label="Intake"
              outlined
              dense
              hide-details
              class="ri-gen__field"
              @change="loadWorking"
            />
            <v-menu
              v-model="fromMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="form.from_date"
                  label="From"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="ri-gen__field"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.from_date"
                no-title
                @input="(fromMenu = false), loadWorking()"
              />
            </v-menu>
            <v-menu
              v-model="toMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="form.to_date"
                  label="To"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="ri-gen__field"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.to_date"
                no-title
                @input="(toMenu = false), loadWorking()"
              />
            </v-menu>
            <v-menu
              v-model="invoiceMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="form.invoice_date"
                  label="Invoice Date"
                  readonly
                  outlined
                  dense
                  hide-details
                  class="ri-gen__field"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="form.invoice_date"
                no-title
                @input="invoiceMenu = false"
              />
            </v-menu>
          </div>

          <div v-if="workingLoading" class="ri-state">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
              width="3"
            />
            <p class="ri-state__text">Working out the period…</p>
          </div>

          <template v-else-if="working">
            <v-alert
              v-if="!selectableLines.length && !raisedLines.length"
              dense
              text
              type="warning"
              class="mb-0"
            >
              Nothing to invoice for this window.
            </v-alert>

            <v-alert
              v-else-if="!selectableLines.length"
              dense
              text
              type="info"
              class="mb-0"
            >
              Every category with a base has already been invoiced for this
              window.
            </v-alert>

            <template v-else>
              <p class="ri-gen__hint">
                Tick the categories to raise. Numbers are taken in the order
                shown, and a category can only be raised once per window.
              </p>

              <table class="ri-gen__table">
                <thead>
                  <tr>
                    <th class="ri-gen__check">
                      <v-simple-checkbox
                        :value="allSelected"
                        :indeterminate="someSelected"
                        :ripple="false"
                        color="primary"
                        @input="toggleAll"
                      />
                    </th>
                    <th>Invoice</th>
                    <th>Description</th>
                    <th class="ri-num">Net</th>
                    <th class="ri-num">GST</th>
                    <th class="ri-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="line in selectableLines"
                    :key="line.key"
                    :class="{
                      'ri-gen__row--off': !selectedCategories.includes(
                        line.key,
                      ),
                    }"
                  >
                    <td class="ri-gen__check">
                      <v-simple-checkbox
                        :value="selectedCategories.includes(line.key)"
                        :ripple="false"
                        color="primary"
                        @input="toggle(line.key)"
                      />
                    </td>
                    <td>
                      <span class="ri-gen__number">
                        {{ numberFor(line.key) || "—" }}
                      </span>
                      <span class="ri-gen__cat">{{ line.name }}</span>
                    </td>
                    <td>
                      <v-text-field
                        v-model="descriptions[line.key]"
                        :placeholder="defaultDescription(line)"
                        outlined
                        dense
                        hide-details
                        class="ri-gen__desc"
                      />
                    </td>
                    <td class="ri-num">{{ money(line.net_amount) }}</td>
                    <td class="ri-num">{{ money(line.gst_amount) }}</td>
                    <td class="ri-num ri-strong">
                      {{ money(line.total_amount) }}
                    </td>
                  </tr>
                  <tr
                    v-for="line in raisedLines"
                    :key="line.key"
                    class="ri-gen__row--done"
                  >
                    <td class="ri-gen__check">
                      <v-icon x-small color="success">mdi-check-circle</v-icon>
                    </td>
                    <td>
                      <span class="ri-gen__cat">{{ line.name }}</span>
                      <span class="ri-gen__done"
                        >already raised for this window</span
                      >
                    </td>
                    <td colspan="4"></td>
                  </tr>
                </tbody>
              </table>
            </template>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <span v-if="!profileReady" class="ri-gen__warn">
            <v-icon x-small color="warning" class="mr-1"
              >mdi-alert-outline</v-icon
            >
            Set the billing profile first
          </span>
          <v-spacer />
          <v-btn
            text
            small
            :disabled="generating"
            @click="generateDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            small
            depressed
            color="primary"
            :loading="generating"
            :disabled="!selectedCategories.length || !profileReady"
            @click="generate"
          >
            Raise {{ selectedCategories.length }} Invoice{{
              selectedCategories.length === 1 ? "" : "s"
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" max-width="820px" scrollable>
      <v-card v-if="selected">
        <v-card-title class="font-weight-medium secondary--text primary py-3">
          <span>{{ selected.invoice_number }}</span>
          <v-spacer />
          <v-btn icon x-small @click="previewDialog = false">
            <v-icon x-small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="ri-preview">
          <ServiceInvoicePrint
            ref="printable"
            :document="selected"
            show-working
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text small @click="previewDialog = false">Close</v-btn>
          <v-btn
            small
            depressed
            color="primary"
            :loading="downloading"
            @click="downloadPdf(selected)"
          >
            <v-icon small left>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="420px">
      <v-card v-if="selected">
        <v-card-title class="font-weight-medium secondary--text primary py-3">
          <span>Cancel {{ selected.invoice_number }}?</span>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="ri-cancel__hint">
            The number stays used — an auditor expects to see the gap explained,
            not closed.
          </p>
          <v-text-field
            v-model.trim="cancelReason"
            label="Reason"
            outlined
            dense
            hide-details="auto"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text small @click="cancelDialog = false">Keep</v-btn>
          <v-btn
            small
            depressed
            color="error"
            :loading="cancelling"
            @click="cancelInvoice"
          >
            Cancel Invoice
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="ri-offscreen">
      <ServiceInvoicePrint
        v-if="exportDocument"
        ref="exportable"
        :document="exportDocument"
      />
    </div>

    <BillingProfileDialog
      v-model="billingDialog"
      :partner-id="partnerId"
      :partner-name="partnerName"
      :partners="partners"
      @saved="loadProfile"
    />
  </div>
</template>

<script>
import BillingProfileDialog from "@/components/revenue/BillingProfileDialog.vue";
import ServiceInvoicePrint from "@/components/revenue/ServiceInvoicePrint.vue";

export default {
  name: "Revenue Invoices",
  components: { BillingProfileDialog, ServiceInvoicePrint },

  data() {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      partners: [],
      programBatches: [],
      documents: [],
      profile: null,
      working: null,
      partnerId: null,
      search: "",
      loading: false,
      workingLoading: false,
      generating: false,
      downloading: false,
      cancelling: false,
      generateDialog: false,
      previewDialog: false,
      cancelDialog: false,
      billingDialog: false,
      fromMenu: false,
      toMenu: false,
      invoiceMenu: false,
      selected: null,
      exportDocument: null,
      cancelReason: "",
      descriptions: {},
      selectedCategories: [],
      form: {
        batch: null,
        from_date: this.toIso(first),
        to_date: this.toIso(last),
        invoice_date: this.toIso(today),
        include_prior_unbilled: false,
      },
    };
  },

  computed: {
    partnerName() {
      return (
        this.partners.find((item) => item.id === this.partnerId)?.name || ""
      );
    },

    profileReady() {
      return Boolean(this.profile?.is_saved && this.profile?.legal_name);
    },

    batchItems() {
      const seen = new Map();

      this.programBatches.forEach((batch) => {
        const value = `${batch.year}-${batch.batch}`;
        if (seen.has(value)) return;
        seen.set(value, {
          value,
          label: `${batch.batch === 2 ? "Jul" : "Jan"} ${String(
            batch.year,
          ).slice(-2)} Batch`,
          year: batch.year,
          batch: batch.batch,
        });
      });

      return [
        { value: null, label: "All intakes" },
        ...[...seen.values()].sort(
          (a, b) => b.year - a.year || b.batch - a.batch,
        ),
      ];
    },

    selectedBatch() {
      return this.batchItems.find((item) => item.value === this.form.batch);
    },

    chargeableLines() {
      return (this.working?.categories || []).filter(
        (line) => Number(line.gross_amount) > 0,
      );
    },

    selectableLines() {
      return this.chargeableLines.filter((line) => !line.already_raised);
    },

    raisedLines() {
      return (this.working?.categories || []).filter(
        (line) => line.already_raised,
      );
    },

    allSelected() {
      return (
        this.selectableLines.length > 0 &&
        this.selectedCategories.length === this.selectableLines.length
      );
    },

    someSelected() {
      return this.selectedCategories.length > 0 && !this.allSelected;
    },

    totalRaised() {
      return this.documents
        .filter((document) => document.status !== "cancelled")
        .reduce((sum, document) => sum + Number(document.total_amount || 0), 0);
    },

    groupedRuns() {
      const term = (this.search || "").trim().toLowerCase();
      const runs = new Map();

      this.documents
        .filter((document) => {
          if (!term) return true;
          return `${document.invoice_number} ${document.category_name}`
            .toLowerCase()
            .includes(term);
        })
        .forEach((document) => {
          const period = document.period || {};
          if (!runs.has(document.run_id)) {
            runs.set(document.run_id, {
              run_id: document.run_id,
              periodLabel: period.label || "—",
              batchLabel: period.batch_label || "All intakes",
              invoiceDate: document.invoice_date,
              documents: [],
              total: 0,
            });
          }

          const run = runs.get(document.run_id);
          run.documents.push(document);
          if (document.status !== "cancelled") {
            run.total += Number(document.total_amount || 0);
          }
        });

      return [...runs.values()].sort(
        (a, b) => new Date(b.invoiceDate) - new Date(a.invoiceDate),
      );
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Revenue Split", to: { name: "Revenue Setup" } },
      {
        text: "Revenue Summary",
        to: {
          name: "Revenue Summary",
          query: { partner_id: this.$route.query.partner_id },
        },
      },
      { text: "Revenue Invoices" },
    ]);

    const query = this.$route.query;
    if (query.partner_id) this.partnerId = Number(query.partner_id);
    if (query.from_date) this.form.from_date = query.from_date;
    if (query.to_date) this.form.to_date = query.to_date;
    if (query.batch) this.form.batch = query.batch;
    if (query.sweep) this.form.include_prior_unbilled = true;

    this.initialise();
  },

  methods: {
    async initialise() {
      await Promise.all([this.getPartners(), this.getBatches()]);
      if (this.partnerId) await this.onPartnerChange();
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = (res.data.data || []).filter(
          (partner) => partner.type !== "Sponsor",
        );
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getBatches() {
      try {
        const res = await this.$axios.get("/program_batches");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
      }
    },

    toggle(key) {
      const index = this.selectedCategories.indexOf(key);
      if (index === -1) this.selectedCategories.push(key);
      else this.selectedCategories.splice(index, 1);
    },

    toggleAll() {
      this.selectedCategories = this.allSelected
        ? []
        : this.selectableLines.map((line) => line.key);
    },

    numberFor(key) {
      const position = this.selectedCategories
        .slice()
        .sort(
          (a, b) =>
            this.selectableLines.findIndex((l) => l.key === a) -
            this.selectableLines.findIndex((l) => l.key === b),
        )
        .indexOf(key);

      return position === -1 ? null : this.nextNumber(position);
    },

    async onPartnerChange() {
      await Promise.all([this.loadDocuments(), this.loadProfile()]);
    },

    async loadDocuments() {
      if (!this.partnerId) return;
      this.loading = true;
      try {
        const res = await this.$axios.get("/revenue_invoices", {
          params: { partner_id: this.partnerId },
        });
        this.documents = res.data.data || [];
      } catch (err) {
        this.documents = [];
        this.notifyError(err, "Unable to load invoices");
      } finally {
        this.loading = false;
      }
    },

    async loadProfile() {
      if (!this.partnerId) return;
      try {
        const res = await this.$axios.get("/revenue_billing_profile", {
          params: { partner_id: this.partnerId },
        });
        this.profile = res.data.data;
      } catch (err) {
        this.profile = null;
      }
    },

    openGenerate() {
      this.generateDialog = true;
      this.descriptions = {};
      this.selectedCategories = [];
      this.loadWorking();
    },

    async loadWorking() {
      if (!this.partnerId) return;
      this.workingLoading = true;
      try {
        const params = {
          partner_id: this.partnerId,
          from_date: this.form.from_date,
          to_date: this.form.to_date,
        };

        if (this.selectedBatch?.year) {
          params.batch_year = this.selectedBatch.year;
          params.batch_no = this.selectedBatch.batch;
        }

        if (this.form.include_prior_unbilled) {
          params.include_prior_unbilled = true;
        }

        const res = await this.$axios.get("/revenue_workings", { params });
        this.working = res.data.data;

        this.selectedCategories = this.selectableLines.map((line) => line.key);
      } catch (err) {
        this.working = null;
        this.notifyError(err, "Unable to work out this period");
      } finally {
        this.workingLoading = false;
      }
    },

    nextNumber(index) {
      const next = Number(this.profile?.last_invoice_number || 0) + index + 1;
      const padded = String(next).padStart(
        Number(this.profile?.invoice_number_padding) || 3,
        "0",
      );
      const prefix = this.profile?.invoice_prefix
        ? `${this.profile.invoice_prefix}/`
        : "";
      const date = new Date(this.form.invoice_date);
      const start =
        date.getMonth() + 1 >= 4 ? date.getFullYear() : date.getFullYear() - 1;
      return `${prefix}${padded}/${start}-${start + 1}`;
    },

    defaultDescription(line) {
      const batch = this.working?.batch?.label || "";
      const period = this.working?.period?.label || "";
      return `${line.name} for the ${batch} — collections from ${period}`;
    },

    async generate() {
      this.generating = true;
      try {
        const payload = {
          partner_id: this.partnerId,
          from_date: this.form.from_date,
          to_date: this.form.to_date,
          invoice_date: this.form.invoice_date,
          include_prior_unbilled: this.form.include_prior_unbilled,
          descriptions: this.descriptions,
          category_keys: this.selectedCategories,
        };

        if (this.selectedBatch?.year) {
          payload.batch_year = this.selectedBatch.year;
          payload.batch_no = this.selectedBatch.batch;
        }

        const res = await this.$axios.post("/revenue_invoices", payload);
        const count = (res.data.data?.documents || []).length;

        this.$store.commit("showSnackbar", {
          message: `${count} invoice${count === 1 ? "" : "s"} raised`,
          color: "success",
        });

        this.generateDialog = false;
        await Promise.all([this.loadDocuments(), this.loadProfile()]);
      } catch (err) {
        this.notifyError(err, "Unable to raise the invoices");
      } finally {
        this.generating = false;
      }
    },

    preview(document) {
      this.selected = document;
      this.previewDialog = true;
    },

    confirmCancel(document) {
      this.selected = document;
      this.cancelReason = "";
      this.cancelDialog = true;
    },

    async cancelInvoice() {
      this.cancelling = true;
      try {
        await this.$axios.delete(`/revenue_invoices/${this.selected.id}`, {
          params: { reason: this.cancelReason },
        });

        this.$store.commit("showSnackbar", {
          message: "Invoice cancelled",
          color: "success",
        });

        this.cancelDialog = false;
        await this.loadDocuments();
      } catch (err) {
        this.notifyError(err, "Unable to cancel the invoice");
      } finally {
        this.cancelling = false;
      }
    },

    async downloadWorking(runId) {
      try {
        const res = await this.$axios.get(
          `/revenue_invoice_runs/${runId}/download`,
          { responseType: "blob" },
        );

        const link = document.createElement("a");
        link.href = URL.createObjectURL(
          new Blob([res.data], { type: "text/csv" }),
        );
        link.download = `revenue_working_${runId}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
      } catch (err) {
        this.notifyError(err, "Unable to download the working");
      }
    },

    async downloadPdf(document) {
      this.downloading = true;
      try {
        const onScreen =
          this.previewDialog && this.selected?.id === document.id;
        if (!onScreen) {
          this.exportDocument = document;
          await this.$nextTick();
        }

        const component = onScreen
          ? this.$refs.printable
          : this.$refs.exportable;
        const element = component?.$el || component;
        if (!element) return;

        const html2canvas = (await import("html2canvas")).default;
        const { default: jsPDF } = await import("jspdf");

        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
        });

        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 8;
        const usableWidth = pageWidth - margin * 2;
        const imageHeight = (canvas.height * usableWidth) / canvas.width;

        pdf.addImage(
          canvas.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          usableWidth,
          imageHeight,
        );

        pdf.save(`${document.invoice_number.replace(/\//g, "_")}.pdf`);
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: "Unable to build the PDF",
          color: "error",
        });
      } finally {
        this.exportDocument = null;
        this.downloading = false;
      }
    },

    money(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    date(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    toIso(date) {
      const offset = date.getTimezoneOffset() * 60000;
      return new Date(date.getTime() - offset).toISOString().slice(0, 10);
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      const changeset = data?.errors
        ? Object.entries(data.errors)
            .map(
              ([field, messages]) =>
                `${field} ${[].concat(messages).join(", ")}`,
            )
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changeset || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.ri-page {
  --ri-line: #e4e7ec;
  --ri-ink: #101828;
  --ri-ink-2: #475467;
  --ri-ink-3: #98a2b3;
  padding: 16px 20px 40px;
  min-height: 100%;
}

.ri-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.ri-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--ri-ink);
  letter-spacing: -0.2px;
}

.ri-header__sub {
  margin: 4px 0 0;
  max-width: 640px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ri-ink-2);
}

.ri-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
}

.ri-field {
  max-width: 220px;
  flex: 0 1 220px;
}

.ri-field--wide {
  max-width: 280px;
  flex: 0 1 280px;
}

.ri-summary {
  display: flex;
  gap: 16px;
  font-size: 12.5px;
  color: var(--ri-ink-2);
}

.ri-summary strong {
  color: var(--ri-ink);
  font-variant-numeric: tabular-nums;
}

.ri-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
}

.ri-state--card {
  margin-top: 12px;
  border-radius: 10px;
  background: #fff;
}

.ri-state__title {
  margin: 14px 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--ri-ink);
}

.ri-state__text {
  margin: 0;
  max-width: 520px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--ri-ink-2);
}

.ri-run {
  margin-top: 14px;
  background: #fff;
  border: 1px solid var(--ri-line);
  border-radius: 10px;
  overflow: hidden;
}

.ri-run__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 12px 16px;
  border-bottom: 1px solid var(--ri-line);
  background: #fbfcfd;
}

.ri-run__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--ri-ink);
}

.ri-run__sub {
  margin: 2px 0 0;
  font-size: 12px;
  color: var(--ri-ink-2);
}

.ri-run__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ri-run__total {
  font-size: 16px;
  font-weight: 700;
  color: var(--ri-ink);
  font-variant-numeric: tabular-nums;
}

.ri-docs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1px;
  background: var(--ri-line);
}

.ri-doc {
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  background: #fff;
}

.ri-doc--cancelled {
  background: #fffbfa;
}

.ri-doc--cancelled .ri-doc__number,
.ri-doc--cancelled .ri-doc__total {
  text-decoration: line-through;
  color: var(--ri-ink-3);
}

.ri-doc__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ri-doc__number {
  font-size: 13px;
  font-weight: 700;
  color: var(--ri-ink);
  font-variant-numeric: tabular-nums;
}

.ri-doc__category {
  margin: 4px 0 0;
  font-size: 12px;
  color: var(--ri-ink-2);
  line-height: 1.4;
  min-height: 34px;
}

.ri-doc__amounts {
  margin: 6px 0 0;
  display: flex;
  flex-direction: column;
}

.ri-doc__total {
  font-size: 17px;
  font-weight: 700;
  color: var(--ri-ink);
  font-variant-numeric: tabular-nums;
}

.ri-doc__split {
  font-size: 11px;
  color: var(--ri-ink-3);
}

.ri-doc__actions {
  display: flex;
  gap: 2px;
  margin: 8px 0 -6px -8px;
}

.ri-gen {
  padding-top: 16px !important;
  min-height: 260px;
}

.ri-gen__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 14px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--ri-line);
}

.ri-gen__field {
  max-width: 155px;
  flex: 0 1 155px;
}

.ri-gen__check {
  margin: 0;
  padding: 0;
}

.ri-gen__check ::v-deep .v-label {
  font-size: 12.5px;
}

.ri-gen__hint {
  margin: 0 0 10px;
  font-size: 12.5px;
  color: var(--ri-ink-2);
}

.ri-gen__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
}

.ri-gen__table th {
  padding: 7px 8px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--ri-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--ri-line);
}

.ri-gen__table td {
  padding: 8px;
  border-bottom: 1px solid #f4f5f7;
  color: var(--ri-ink-2);
  vertical-align: middle;
}

.ri-gen__check {
  width: 42px;
  text-align: center;
}

.ri-gen__row--off {
  opacity: 0.45;
}

.ri-gen__row--done {
  background: #f6fef9;
}

.ri-gen__done {
  display: block;
  font-size: 11px;
  color: #067647;
}

.ri-gen__number {
  display: block;
  font-weight: 700;
  color: var(--ri-ink);
  font-variant-numeric: tabular-nums;
}

.ri-gen__cat {
  display: block;
  font-size: 11px;
  color: var(--ri-ink-3);
}

.ri-gen__desc ::v-deep .v-input__slot {
  min-height: 32px !important;
}

.ri-gen__desc ::v-deep input {
  font-size: 12px;
}

.ri-gen__warn {
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #b54708;
}

.ri-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ri-strong {
  font-weight: 700;
  color: var(--ri-ink);
}

.ri-preview {
  background: #f2f4f7;
  padding: 18px !important;
}

.ri-cancel__hint {
  margin: 0 0 14px;
  font-size: 12.5px;
  color: var(--ri-ink-2);
}

.ri-offscreen {
  position: fixed;
  left: -10000px;
  top: 0;
  width: 760px;
}

@media (max-width: 600px) {
  .ri-page {
    padding: 12px 10px 40px;
  }

  .ri-field,
  .ri-field--wide,
  .ri-gen__field {
    max-width: none;
    flex: 1 1 100%;
  }
}
</style>
